import { useEffect, useRef, useState } from 'react';
import { useGlobalStateSelector, words } from './globalState';
import { handleClickAuthenticate } from './KeyBox';
import { SelectWalletBox, SignIcon, SelectSessionButton } from './KeysAndSigningUI';
import { handleClickLogin } from './login';
import {
  decryptNfcRecords,
  prepareNfcrwScan,
  readAndClearNfcrwCallbackFromLocalStorage,
} from './nativeApp';
import {
  walletsAvailable,
  keepKeyPairsInMemory,
  keepKeyPairsAndSubAddressesInMemory,
  getSecretsFromNfc,
  getSecretsFromNfcNativeKeyCard,
  getSecretsFromQr,
  getValidKeyPairs,
} from './keysAndSigning';

import iconCheck from './image/check_24dp_FILL0_wght400_GRAD0_opsz24.svg';

// LOGIN POPUP
function LoginPopup(props) {
  const clientParameters = useGlobalStateSelector( ({ state }) => state.config.clientParameters );
  const login = useGlobalStateSelector( ({ state }) => state.login );
  const keyPairsInPoolAll = useGlobalStateSelector( ({ state }) => state.keyPairs );
  const session = useGlobalStateSelector( ({ state }) => state.session );
  const marginOfSessionTime = useGlobalStateSelector( ({ state }) => state.marginOfSessionTime );
  const walletType = useGlobalStateSelector( ({ state }) => state.walletType );
  const assetInfos = useGlobalStateSelector( ({ state }) => state.assetInfo );
  const monacards = useGlobalStateSelector( ({ state }) => state.monacard );
  const network = useGlobalStateSelector( ({ state }) => state.networkMona );
  const paths = useGlobalStateSelector( ({ state }) => state.configKB.clientParameters.paths );
  const device = useGlobalStateSelector( ({ state }) => state.device );
  const language = useGlobalStateSelector( ({ state }) => state.language || 'english' );
  const dispatch = useGlobalStateSelector( ({ dispatch }) => dispatch );
  const popupLayer = props.popupLayer;
  const periodToKeepKeyPairs = clientParameters.periodToKeepKeyPairs;
  const pass = clientParameters.specialMessage;
  const [nfcrwScanLink, setNfcrwScanLink] = useState(null);
  const nfcrwScanSessionRef = useRef(null);
  const expectedNfcrwScanStateRef = useRef(null);
  const nfcrwLoginRequestedRef = useRef(false);

  // タップ時に同期的に href を確定できるよう、暗号化スキャン用 Universal Link を事前に組み立てておく
  useEffect( () => {
    if (walletType !== 'nfcNative') {
      return;
    }

    if (!nfcrwScanSessionRef.current) {
      prepareNfcrwScan({ language }).then( ({ session, state, scanLink }) => {
        nfcrwScanSessionRef.current = session;
        expectedNfcrwScanStateRef.current = state;
        setNfcrwScanLink(scanLink);
      });
    }
  }, [walletType, language]);

  // ネイティブアプリからのスキャン結果は、起き上がった元タブが localStorage から自分で読む
  useEffect( () => {
    if (walletType !== 'nfcNative') {
      return;
    }

    const consumeScanCallback = async () => {
      if (!nfcrwLoginRequestedRef.current) {
        return;
      }

      const expectedState = expectedNfcrwScanStateRef.current;

      if (!expectedState) {
        return;
      }

      const payload = readAndClearNfcrwCallbackFromLocalStorage({ state: expectedState });

      if (!payload) {
        return;
      }

      const session = nfcrwScanSessionRef.current;

      nfcrwLoginRequestedRef.current = false;
      expectedNfcrwScanStateRef.current = null;
      nfcrwScanSessionRef.current = null;
      setNfcrwScanLink(null);

      prepareNfcrwScan({ language }).then( ({ session, state, scanLink }) => {
        nfcrwScanSessionRef.current = session;
        expectedNfcrwScanStateRef.current = state;
        setNfcrwScanLink(scanLink);
      });

      if (payload.error || !payload.pubkey || !payload.ciphertext || !session) {
        dispatch({ type: 'setNotification', key: 'notification', value: words.failedToReadNfc[language] });
        return;
      }

      let records;

      try {
        ({ records } = await decryptNfcRecords({
          keyPair: session.keyPair,
          browserPublicKey: session.browserPublicKey,
          appPublicKeyB64: payload.pubkey,
          ciphertextB64: payload.ciphertext,
          state: expectedState,
        }));
      }
      catch {
        dispatch({ type: 'setNotification', key: 'notification', value: words.failedToReadNfc[language] });
        return;
      }

      try {
        const { mnemonic, keyPairs, addressMain } = await getSecretsFromNfcNativeKeyCard({ dispatch, pass, records, language, network });

        handleClickLogin({
          dispatch,
          popupLayer,
          keyPairs,
          keyPairsInPoolAll,
          sessionToOverride: {},
          login,
          walletType,
          session,
          marginOfSessionTime,
          assetInfos,
          monacards,
          network,
          clientParameters,
          language,
        })
        .then( result => {
          dispatch({ type: 'setStateMultiLayers', keys: ['active', 'parent'], value: result.activeAddress });

          if (result.loginAddressCount.signature >= 1) {
            dispatch({ type: 'setNotification', key: 'notification', value: words.loggedIn[language] });
          }
        })
        .catch( error => {
          let errorMessage;
          try {
            errorMessage = JSON.parse(error.message);
          }
          catch (error) {
            dispatch({ type: 'setNotification', key: 'notification', value: `${words.error[language]}: ${error.message}` });
          }

          if (errorMessage.notification === 'failedToLogin') {
            dispatch({ type: 'setNotification', key: 'notification', value: words.failedToLogin[language] });
          }
        });

        const mnemonicRecords = [{ mnemonic, address: addressMain }];
        keepKeyPairsAndSubAddressesInMemory({ dispatch, mnemonicRecords, paths, periodToKeepKeyPairs, language, network });
      }
      catch {
        return;
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        consumeScanCallback();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', consumeScanCallback);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', consumeScanCallback);
    };
  }, [walletType, dispatch, pass, language, network, popupLayer, keyPairsInPoolAll, login, session, marginOfSessionTime, assetInfos, monacards, clientParameters, paths, periodToKeepKeyPairs]);

  let loginSection;

  // ログインボタンクリック関数

  let onClickLoginFunction;

  if (walletType === 'keybox') {
    // disabled={ state.passkey.busy }
    onClickLoginFunction = async () => {
      try {
        dispatch({ type: 'setStateMultiLayers', keys: ['passkey', 'busy'], value: true });
        const {
          decryptedMnemonicRecords: mnemonicRecords,
          decryptedWifRecords: wifRecords,
          keyPairs,
        } = await handleClickAuthenticate({ dispatch, network, language });

        handleClickLogin({
          dispatch,
          popupLayer,
          keyPairs,
          keyPairsInPoolAll,
          sessionToOverride: {},
          login,
          walletType,
          session,
          marginOfSessionTime,
          assetInfos,
          monacards,
          network,
          clientParameters,
          language,
        })
        .then( result => {
          dispatch({ type: 'setStateMultiLayers', keys: ['active', 'parent'], value: result.activeAddress });

          if (result.loginAddressCount.signature >= 1) {
            dispatch({ type: 'setNotification', key: 'notification', value: words.loggedIn[language] });
          }
        })
        .catch( error => {
          let errorMessage;
          try {
            errorMessage = JSON.parse(error.message);
          }
          catch (error) {
            dispatch({ type: 'setNotification', key: 'notification', value: `${words.error[language]}: ${error.message}` });
          }

          if (errorMessage?.notification === 'failedToLogin') {
            dispatch({ type: 'setNotification', key: 'notification', value: words.failedToLogin[language] });
          }
        });

        keepKeyPairsAndSubAddressesInMemory({ dispatch, mnemonicRecords, paths, periodToKeepKeyPairs, language, network });
        keepKeyPairsInMemory({
          dispatch,
          keyPairs: wifRecords.map( (wifRecord) => wifRecord.keyPair ),
          periodToKeepKeyPairs,
          language,
          network,
        });
      }
      finally {
        dispatch({ type: 'setStateMultiLayers', keys: ['passkey', 'busy'], value: false });
      }
    };
  }
  else if (walletType === 'mpurse') {
    onClickLoginFunction = () => {
      handleClickLogin({
        dispatch,
        popupLayer,
        keyPairs: undefined,
        keyPairsInPoolAll,
        sessionToOverride: {},
        login,
        walletType,
        session,
        marginOfSessionTime,
        assetInfos,
        monacards,
        network,
        clientParameters,
        language,
      })
      .then( result => {
        dispatch({ type: 'setStateMultiLayers', keys: ['active', 'parent'], value: result.activeAddress });

        if (result.loginAddressCount.signature >= 1) {
          dispatch({ type: 'setNotification', key: 'notification', value: words.loggedIn[language] });
        }
      })
      .catch( error => {
        let errorMessage;
        try {
          errorMessage = JSON.parse(error.message);
        }
        catch (error) {
          dispatch({ type: 'setNotification', key: 'notification', value: `${words.error[language]}: ${error.message}` });
        }

        if (errorMessage.notification === 'failedToLogin') {
          dispatch({ type: 'setNotification', key: 'notification', value: words.failedToLogin[language] });
        }
      });
    };
  }
  else if (walletType === 'nfc') {
    onClickLoginFunction = async () => {
      dispatch({ type: 'setNotification', key: 'notification', value: words.touchWithYourKeyCard[language] });
      const { mnemonic, keyPairs, addressMain } = await getSecretsFromNfc({ dispatch, pass, language, network });

      handleClickLogin({
        dispatch,
        popupLayer,
        keyPairs,
        keyPairsInPoolAll,
        sessionToOverride: {},
        login,
        walletType,
        session,
        marginOfSessionTime,
        assetInfos,
        monacards,
        network,
        clientParameters,
        language,
      })
      .then( result => {
        dispatch({ type: 'setStateMultiLayers', keys: ['active', 'parent'], value: result.activeAddress });

        if (result.loginAddressCount.signature >= 1) {
          dispatch({ type: 'setNotification', key: 'notification', value: words.loggedIn[language] });
        }
      })
      .catch( error => {
        let errorMessage;
        try {
          errorMessage = JSON.parse(error.message);
        }
        catch (error) {
          dispatch({ type: 'setNotification', key: 'notification', value: `${words.error[language]}: ${error.message}` });
        }

        if (errorMessage.notification === 'failedToLogin') {
          dispatch({ type: 'setNotification', key: 'notification', value: words.failedToLogin[language] });
        }
      });

      const mnemonicRecords = [{ mnemonic, address: addressMain }];
      keepKeyPairsAndSubAddressesInMemory({ dispatch, mnemonicRecords, paths, periodToKeepKeyPairs, language, network });
    };
  }
  else if (walletType === 'nfcNative') {
    // ログインボタンはネイティブアプリ起動用の Universal Link に置き換える
  }
  else if (walletType === 'qr') {
    onClickLoginFunction = async () => {
      dispatch({ type: 'setNotification', key: 'notification', value: words.scanYour2DBarcode[language] });
      const { mnemonic, keyPairs, addressMain } = await getSecretsFromQr({ dispatch, pass, popupLayer, language, network });

      handleClickLogin({
        dispatch,
        popupLayer,
        keyPairs,
        keyPairsInPoolAll,
        sessionToOverride: {},
        login,
        walletType,
        session,
        marginOfSessionTime,
        assetInfos,
        monacards,
        network,
        clientParameters,
        language,
      })
      .then( result => {
        dispatch({ type: 'setStateMultiLayers', keys: ['active', 'parent'], value: result.activeAddress });

        if (result.loginAddressCount.signature >= 1) {
          dispatch({ type: 'setNotification', key: 'notification', value: words.loggedIn[language] });
        }
      })
      .catch( error => {
        let errorMessage;
        try {
          errorMessage = JSON.parse(error.message);
        }
        catch (error) {
          dispatch({ type: 'setNotification', key: 'notification', value: `${words.error[language]}: ${error.message}` });
        }

        if (errorMessage.notification === 'failedToLogin') {
          dispatch({ type: 'setNotification', key: 'notification', value: words.failedToLogin[language] });
        }
      });

      const mnemonicRecords = [{ mnemonic, address: addressMain }];
      keepKeyPairsAndSubAddressesInMemory({ dispatch, mnemonicRecords, paths, periodToKeepKeyPairs, language, network });
    };
  }
  else {
    onClickLoginFunction = () => {
      const walletType = walletsAvailable({ hasCamera: device?.hasCamera })[0];
      dispatch({ type: 'setState', key: 'walletType', value: walletType });
    };
  }

  // ログインセクション

  let loginButton;

  if (walletType === 'nfcNative') {
    loginButton =
    <a className='buttonMainColor widthMin20 heightMin4 flexRow justifyContentCenter alignItemsCenter ' tabindex='0'
      href={ nfcrwScanLink }
      onClick={ (event) => {
        if (!nfcrwScanLink) {
          event.preventDefault();
          return;
        }

        nfcrwLoginRequestedRef.current = true;
        dispatch({ type: 'setNotification', key: 'notification', value: words.touchWithYourKeyCard[language] });
      }}
    >
      <div>
        { words.login[language] }
      </div>
      <SignIcon />
    </a>;
  }
  else {
    loginButton =
    <button className='buttonMainColor widthMin20 heightMin4 flexRow justifyContentCenter alignItemsCenter ' tabindex='0' onClick={ onClickLoginFunction } >
      <div>
        { words.login[language] }
      </div>
      <SignIcon />
    </button>;
  }

  loginSection =
  <div>
    <div className='flexColumn alignItemsCenter marginTop0p5'>
      {/* addressSection */}
      { loginButton }
      <div className='flexRow justifyContentCenter alignItemsCenter marginTop0p5'>
        <SelectSessionButton popupLayer={popupLayer} loginEnabled={true} setConfigure={true} />
        {/*
          <button className={'button1'} tabindex='0'
            onClick={ () => {
              dispatch({ type: 'setStateMultiLayers', keys: ['login', 'displayAddressSection'], value: login.displayAddressSection ? false : true });
            }}
          >
            <img className='size2x2' src={switchIcon} alt='' />
          </button>
        */}
      </div>
    </div>
    {/*
      {/ SP /}
      <div className='visibleSmallOrLess flexColumn alignItemsFlexStart marginTop0p5'>
        <TextLine3 fieldName='loginAddressMain' keys={['login', 'addressMain']} face={words.login[language]} tooltip='right' />
        <div className='flexRow justifyContentFlexStart alignItemsCenter '>
          <button className='button1' onClick={ () => handleClickMpurse(state, dispatch, ['login', 'addressMain']) }>
            <img className='size2x2' src={iconMpurse} alt='' />
          </button>
          <button className='button1' tabindex='0'
            onClick={ () => {
              handleClickLogin({
                dispatch,
                popupLayer,
                keyPairs: undefined,
                keyPairsInPoolAll: state.keyPairs,
                sessionToOverride: {},
                login: state.login,
                walletType: state.walletType,
                session: state.session,
                marginOfSessionTime: state.marginOfSessionTime,
                assetInfos: state.assetInfo,
                monacards: state.monacard,
                network: state.networkMona,
                clientParameters: state.config.clientParameters,
                language,
              });
            }}
          >
            <img className='size2x2' src={iconLogin} alt='' />
          </button>
          <SelectSessionButton popupLayer={popupLayer} loginEnabled={true} />
        </div>
      </div>
    */}
  </div>;


  return (
    <div>
      {/* PC */}
      <div className='visibleMiddleOrMore flexColumn alignItemsCenter'>
        <div className="marginBottom1 ">
          <SelectWalletBox />
        </div>
        <div className="marginBottom1 ">
          { loginSection }
        </div>
      </div>
      {/* SP */}
      <div className='visibleSmallOrLess flexColumn alignItemsCenter widthMax'>
        <div className='widthMax padding0p5 marginTopBottom0p5 '>
          <SelectWalletBox />
        </div>
        <div className="">
          { loginSection }
        </div>
      </div>
    </div>
  );
}

// REGISTRATION AND AGREEMENT LIST POPUP ; type: 'registrationAndAgreementListPopup',
function RegistrationAndAgreementListPopup(props) {
  // use-context-selector は selector の戻り値を Object.is で比較するため、
  // ここで新しいオブジェクトを生成して返すと、他の state 変更でも毎回再レンダリングされ得る。
  // 必要な値を個別に購読して、popup / language / clientParameters / dispatch が変わらない限り再レンダリングしない。
  const popup = useGlobalStateSelector(({ state }) => state.popup);
  const language = useGlobalStateSelector(({ state }) => state.language);
  const clientParameters = useGlobalStateSelector(({ state }) => state.config.clientParameters);
  const keyPairsInState = useGlobalStateSelector(({ state }) => state.keyPairs);
  const session = useGlobalStateSelector(({ state }) => state.session);
  const dispatch = useGlobalStateSelector(({ dispatch }) => dispatch);
  const popupLayer = props.popupLayer;
  const popupLayerNext = (popupLayer !== undefined && popupLayer !== null) ? (popupLayer + 1) : 0;
  const addressesThatRequireUserRegistration = popup[popupLayer].body.addressesThatRequireUserRegistration;
  const addressesThatRequireAgreementOfTAC = popup[popupLayer].body.addressesThatRequireAgreementOfTAC;
  const basicInformationThatRequiresAgreementOfTAC = popup[popupLayer].body.basicInformationThatRequiresAgreementOfTAC;
  const applicationMessageParameterThatRequiresAgreementOfTAC = popup[popupLayer].body.applicationMessageParameterThatRequiresAgreementOfTAC;

  const addressesThatRequireUserRegistrationActual = addressesThatRequireUserRegistration.filter(
    address => session[address] === undefined || session[address].expirationOfSession === 0
  );

  const addressesThatRequireAgreementOfTACActual = addressesThatRequireAgreementOfTAC.filter(
    address => session[address] === undefined || session[address].expirationOfSession === 0
  );

  useEffect(() => {
    if (addressesThatRequireUserRegistrationActual.length === 0 && addressesThatRequireAgreementOfTACActual.length === 0) {
      dispatch({ type: 'setStateMultiLayers', keys: ['popup', popupLayer], value: { type: null, body: null } });
    }
  }, [addressesThatRequireUserRegistrationActual, addressesThatRequireAgreementOfTACActual]);

  const addressUnitsThatRequireUserRegistration = addressesThatRequireUserRegistrationActual.map( address =>
    <div key={address} className='flexRow justifyContentFlexStart alignItemsCenter '>
      {address}
      <button className='button1' tabindex='0'
        onClick={ () => {
          // configureをクリアして、ユーザー登録アドレスのみセット
          dispatch({ type: 'setState', key: 'configure', value: { addressMain: address } });

          // keypool に鍵がある場合は、 walletType を keypool に設定
          const keyPairsInPool = getValidKeyPairs({ dispatch, keyPairs: keyPairsInState });
          if (keyPairsInPool[address]?.keyPair !== undefined) {
            dispatch({ type: 'setState', key: 'walletType', value: 'keypool' });
          }

          // ユーザー登録ポップアップを表示
          const popup = { type: 'userRegistrationPopup', mode: 'serviceOnly' };
          dispatch({ type: 'setStateMultiLayers', keys: ['popup', popupLayerNext], value: popup });
        }}
      >
        <img className='size2x2' src={iconCheck} alt='' />
      </button>
    </div>
  );

  const addressUnitsThatRequireAgreementOfTAC = addressesThatRequireAgreementOfTACActual.map( address =>
    <div key={address} className='flexRow justifyContentFlexStart alignItemsCenter '>
      {address}
      <button className='button1' tabindex='0'
        onClick={ () => {
          if ( applicationMessageParameterThatRequiresAgreementOfTAC[address].versionOfTheTermsAndConditions === clientParameters.versionOfTheTermsAndConditions) {
            // // クライアントには最新バージョンがロードされているので、規約同意を促す。
            // dispatch({ type: 'setNotification', key: 'notification', value: words.youMustAcceptTheTermsAndConditionsOfTheCurrentVersion[language] });

            // 基本情報をconfigureにセット（認証は成功しているので、規約同意しやすいように基本情報は埋めてあげる。）
            dispatch({ type: 'setState', key: 'configure', value: basicInformationThatRequiresAgreementOfTAC[address] });

            // keypool に鍵がある場合は、 walletType を keypool に設定
            const keyPairsInPool = getValidKeyPairs({ dispatch, keyPairs: keyPairsInState });
            if (keyPairsInPool[address]?.keyPair !== undefined) {
              dispatch({ type: 'setState', key: 'walletType', value: 'keypool' });
            }

            // 規約同意ポップアップを表示
            const popup = { type: 'userRegistrationPopup', mode: 'serviceOnly' };
            dispatch({ type: 'setStateMultiLayers', keys: ['popup', popupLayerNext], value: popup });
          }
          else {
            // クライアントに最新バージョンがロードされていないので、ブラウザリロードと規約同意を促す。
            dispatch({ type: 'setNotification', key: 'notification', value: words.youMustReloadYourBrowserAndAcceptTheTermsAndConditionsOfTheCurrentVersion[language] });
          }
        }}
      >
        <img className='size2x2' src={iconCheck} alt='' />
      </button>
    </div>
  );

  let addressUnitsThatRequireUserRegistrationTitle;
  if (addressesThatRequireUserRegistrationActual.length >= 1) {
    addressUnitsThatRequireUserRegistrationTitle = words.userRegistrationRequired[language];
  }

  let addressUnitsThatRequireAgreementOfTACTitle;
  if (addressesThatRequireAgreementOfTACActual.length >= 1) {
    addressUnitsThatRequireAgreementOfTACTitle = words.agreementOfTheCurrentVersionOfTACRequired[language];
  }

  return (
    <div>
      <div className='flexColumn alignItemsFlexStart'>
        <div className='flexColumn alignItemsFlexStart'>
          { addressUnitsThatRequireUserRegistrationTitle }
          { addressUnitsThatRequireUserRegistration }
        </div>
        <div className='flexColumn alignItemsFlexStart'>
          { addressUnitsThatRequireAgreementOfTACTitle }
          { addressUnitsThatRequireAgreementOfTAC }
        </div>
      </div>
    </div>
  );
}

export {
  LoginPopup,
  RegistrationAndAgreementListPopup,
};